import { forwardRef, Suspense, useCallback, useImperativeHandle, useState } from 'react';
import { Stack, Text, Icon, Toggle, useTheme, IconButton, ITooltipHostStyles, TooltipHost, Spinner } from '@fluentui/react';
import { getEventTextFromEnum } from '../getEventTextFromEnum';
import { getEventIconFromEnum } from '../getEventIconFromEnum';
import { IntegrationEventType } from '../../../../../api/generated/data-contracts';
import { useId } from '@fluentui/react-hooks';
import { IntegrationCardHandle } from './IntegrationCardHandle';

export const IntegrationCard = forwardRef<
    IntegrationCardHandle,
    {
        eventType: IntegrationEventType;
        enabled: boolean;
        children?: React.ReactNode;
        toggleDisabled?: boolean;
        toggleEvent: (event: IntegrationEventType) => void;
        tooltip?: string;
    }
>(({ enabled, children, toggleEvent, eventType, tooltip, toggleDisabled }, ref) => {
    const [expanded, setExpanded] = useState(enabled);

    const toggle = useCallback((toggle?: boolean) => {
        setExpanded(value => toggle ?? !value);
    }, []);

    const onChange = useCallback(() => {
        toggleEvent(eventType);
    }, [eventType, toggleEvent]);

    useImperativeHandle(
        ref,
        () => {
            return {
                close: () => toggle(false),
                open: () => toggle(true),
                toggle,
            };
        },
        [toggle],
    );

    const theme = useTheme();

    const tooltipHostStyles: Partial<ITooltipHostStyles> = {
        root: { display: 'inline-block', marginLeft: 4 }, // Adjust the margin as needed
    };

    const calloutProps = { gapSpace: 0 };
    const tooltipId = useId('tooltip');
    return (
        <Stack
            styles={{
                root: {
                    width: 250,
                    border: '1px solid rgba(0,0,0,0.3)',
                    padding: 10,
                    backgroundColor: theme.semanticColors.disabledBackground,
                },
            }}
        >
            <Stack horizontal horizontalAlign="space-between" verticalAlign="center">
                <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 10 }} onClick={() => toggle()}>
                    {children ? <IconButton iconProps={{ iconName: expanded ? 'ChevronUp' : 'ChevronDown' }} /> : <div style={{ width: 16 }} />}
                    <Icon iconName={getEventIconFromEnum(eventType)} />
                    <Text>{getEventTextFromEnum(eventType)}</Text>
                    {tooltip && (
                        <TooltipHost id={tooltipId} calloutProps={calloutProps} styles={tooltipHostStyles} content={tooltip}>
                            <IconButton iconProps={{ iconName: 'Info' }} aria-describedby={tooltipId} ariaLabel="Info" title="Info" />
                        </TooltipHost>
                    )}
                </Stack>
                <Stack horizontal verticalAlign="center">
                    <Toggle disabled={toggleDisabled} onChange={onChange} checked={enabled} styles={{ root: { margin: '0 0 0 5px' } }} />
                </Stack>
            </Stack>
            <div
                style={{
                    position: 'relative',
                    maxHeight: expanded ? '150vh' : '0px',
                    overflow: 'hidden',
                    transition: 'all .5s ease',
                }}
            >
                <Suspense fallback={<Spinner />}>{children}</Suspense>
            </div>
        </Stack>
    );
});
